import { HttpClient, HttpEvent, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { retryBackoff } from 'backoff-rxjs';
import { Observable } from 'rxjs';
import { map, timeout } from 'rxjs/operators';
import { AoxMeasurementResult } from 'src/app/interfaces/biozoom-api/aox-measurement-result';
import { HrvMeasurementResult } from 'src/app/interfaces/biozoom-api/hrv-measurement-result';
import { HrvTestMeasurementResult } from 'src/app/interfaces/biozoom-api/hrv-test-measurement-result';
import { PreRequestResult } from 'src/app/interfaces/biozoom-api/pre-request-result';
import { PreciseAoxMeasurementResult } from 'src/app/interfaces/biozoom-api/precise-aox-measurement-result';
import { VersionResult } from 'src/app/interfaces/biozoom-api/version-result';
import { WifiNameResult } from 'src/app/interfaces/biozoom-api/wifi-name-result';

@Injectable({
  providedIn: 'root'
})
/**
 * gatherer class for all http requests to the server
 */
export class ConnectionService {

  constructor(private http: HttpClient) { }

  get = (url: string): Observable<any> => {
    return this.http.get<any>(url);
  }

  /**
   * send a pre-request to the server at the provided url
   * @param url url of the server
   * @returns observable for the server response
   */
  getPrerequest = (url: string): Observable<PreRequestResult> => {
    const headers = {};
    if (url.startsWith('http:')) {
      headers['Access-Control-Allow-Origin'] = '*';
    }
    return this.http.get<PreRequestResult>(url, {headers, observe: 'response'}).pipe(
      map(response => ({
        ...(response.body ?? {}),
        transportHttpStatus: response.status,
      } as PreRequestResult)),
    );
  }

  /**
   * add a timeout to any given request
   * @param request base observable
   * @param t timeout to be added
   * @returns observable for the server response
   */
  addTimeoutToRequest = (
    request: Observable<any>,
    t = 1000,
  ): Observable<any> => {
    return request.pipe(
      timeout(t),

    );
  }

  /**
   * send a request to a server to process a given quick aox measurement
   * use a VWS
   * @param recServer id of the server that is to be used
   * @param vwsMeasurementUrl base url of all VWS
   * @param serial serial ID of the scanner
   * @param form compressed measurement results, including additional data and user language
   * @returns observable for the server response
   */
  getMeasurementAoxVWSRequest = (
    recServer: string,
    vwsMeasurementUrl: string,
    serial: string,
    form: FormData
  ) => {
    let url = `${recServer}${vwsMeasurementUrl}?serial_id=${serial}&measure_type=0`;
    if (!url.startsWith('http')) {
      url = `http://${url}`;
    }
    const headers = {};
    if (url.startsWith('http:')) {
      headers['Access-Control-Allow-Origin'] = '*';
    }
    return this.http.post<AoxMeasurementResult>(
      url,
      form,
      {
        reportProgress: true,
        observe: 'events',
        headers
      }
    );
  }

  /**
   * send a request to a server to process a given precise aox measurement
   * use a VWS
   * @param recServer id of the server that is to be used
   * @param vwsMeasurementUrl base url of all VWS
   * @param serial serial ID of the scanner
   * @param form compressed measurement results, including additional data and user language
   * @returns observable for the server response
   */
  getMeasurementAoxPreciseVWSRequest = (
    recServer: string,
    vwsMeasurementUrl: string,
    serial: string,
    form: FormData
  ) => {
    let url = `${recServer}${vwsMeasurementUrl}?serial_id=${serial}&measure_type=1`;
    if (!url.startsWith('http')) {
      url = `http://${url}`;
    }
    const headers = {};
    if (url.startsWith('http:')) {
      headers['Access-Control-Allow-Origin'] = '*';
    }
    return this.http.post<PreciseAoxMeasurementResult>(
      url,
      form,
      {
        reportProgress: true,
        observe: 'events',
        headers
      }
    );
  }

  /**
   * send a request to a server to process a given quick aox measurement
   * do not use a VWS
   * @param url url of the server
   * @param body body of the request, including measurement results, additional data, and user language
   * @param headers header of the request
   * @returns observable for the server response
   */
  getMeasurementAoxRequest = (
    url: string,
    body: string,
    headers: HttpHeaders
  ): Observable<HttpEvent<AoxMeasurementResult>> => {
    return this.http.post<AoxMeasurementResult>(
      url,
      body,
      {
        headers,
        reportProgress: true,
        observe: 'events'
      }
    );
  }

  /**
   * send a request to a server to process a given precise aox measurement
   * do not use a VWS
   * @param url url of the server
   * @param body body of the request, including measurement results, additional data, and user language
   * @param headers header of the request
   * @returns observable for the server response
   */
  getMeasurementAoxPreciseRequest = (
    url: string,
    body: string,
    headers: HttpHeaders
  ): Observable<HttpEvent<PreciseAoxMeasurementResult>> => {
    return this.http.post<PreciseAoxMeasurementResult>(
      url,
      body,
      {
        headers,
        reportProgress: true,
        observe: 'events'
      }
    );
  }

  /**
   * send a request to a server to process a given hrv measurement
   * @param url url of the server
   * @param body body of the request, including measurement results, additional data, and user language
   * @param headers header of the request
   * @returns observable for the server response
   */
  getMeasurementHrvRequest = (
    url: string,
    body: string,
    headers: HttpHeaders
  ): Observable<HttpEvent<HrvMeasurementResult>> => {
    return this.http.post<HrvMeasurementResult>(
      url,
      body,
      {
        headers,
        reportProgress: true,
        observe: 'events'
      }
    );
  }

  /**
   * send a request to a server to process a given hrv test measurement
   * @param url url of the server
   * @param body body of the request, including measurement results, additional data, and user language
   * @param headers header of the request
   * @returns observable for the server response
   */
  getMeasurementHrvTestRequest = (
    url: string,
    body: string,
    headers: HttpHeaders
  ): Observable<HrvTestMeasurementResult> => {
    return this.http.post<HrvTestMeasurementResult>(
      url,
      body,
      {
        headers,
        observe: 'response',
      }
    ).pipe(
      map(response => ({
        ...(response.body ?? {}),
        transportHttpStatus: response.status,
      } as HrvTestMeasurementResult)),
    );
  }

  /**
   * send a request to a server to fetch the current version of the scanner
   * @param url url of the server
   * @param product identifier for the class of scanners (which company)
   * @returns observable for the server response
   */
  fetchVersion = (
    url: string,
    product: string
  ): Observable<VersionResult> => {
    return this.http.get<VersionResult>(
      `${url}?product=${product}`
    );
  }

  /**
   * send a request to a server to fetch the wifi name of the given scanner
   * @param wifiPrefixUrl base url of the server used to fetch the wifi name
   * @param user username required to authenticate
   * @param password password required to authenticate
   * @param serial serial ID of the scanner
   * @returns observable for the server response
   */
  fetchWiFiName = (
    wifiPrefixUrl: string,
    user: string,
    password: string,
    serial: string
  ): Observable<WifiNameResult> => {
    // const httpOptions = {
    //   headers: new HttpHeaders({
    //     Authorization: 'Basic ' + btoa(
    //       `${user}:${password}`
    //     )
    //   })
    // };
    return this.http.get<WifiNameResult>(
      `${wifiPrefixUrl}/config/wifiPrefix/${serial.toUpperCase()}`,
    );
  }

  /**
   * to any given request, add a maximum number of retries in case it fails
   * @param request base observable
   * @param interval initial interval between retries. goes up over time
   * @param retries maximum amount of retries
   * @returns observable for the server response
   */
  retryRequest = <T>(
    request: Observable<T>,
    interval = 1000,
    retries = 3
  ): Observable<T> => {
    return request.pipe(
      retryBackoff({
        initialInterval: interval,
        maxRetries: retries
      })
    );
  }
}
